import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { styled } from '@mui/system';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, usePermissions } from 'react-admin';
import { Area, AreaChart, Tooltip, XAxis, YAxis } from 'recharts';
import { DashboardWrapper, RotatedAxisTick } from '../../components';
import { PERMISSIONS } from '../../constants';
import useDashboardContext from '../../hooks/useDashboardContext';
import { getAttendantId, getChainId, getEmployeeType } from '../../lib';
import { EmployeeType } from '../../models';
import { formatNumber, getInitialChart } from '../../utils';

const colors = ['#79CF62', '#7869DF', '#D15400', '#E5C025'];
const chartTypes = ['totalValue', 'totalFillins', 'totalAmount', 'discount'];
const chartLabels = ['Valor', 'Abastecimentos', 'Volume', 'Descontos'];
const initialData = { totalValue: 0, totalFillins: 0, totalAmount: 0, discount: 0 };

const chartMargin = { top: 10, right: 24, left: 8, bottom: 10 };
const yAxisDomain = [0, 'auto'];
const axisTick = { fontSize: 12, fill: '#555' };
const axisLine = { stroke: '#ddd' };
const tickLine = { stroke: '#ddd' };
const tooltipStyle = {
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '10px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};
const tooltipLabelStyle = { margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#333' };
const filterStyle = { marginBottom: '20px' };

// Custom styling for Radio Buttons
const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    '& .MuiFormControlLabel-root': {
        flex: '1',
        margin: 0,
        borderRadius: '20px',
        border: '1px solid #ddd',
        transition: 'all 0.3s ease',
        textAlign: 'center',
        justifyContent: 'center',
        padding: '5px 16px',
        backgroundColor: '#f9f9f9',
        '&:hover': {
            backgroundColor: '#f0f0f0',
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-checked': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            borderColor: theme.palette.primary.main,
            fontWeight: 'bold',
        },
    },
    '& .MuiRadio-root': {
        display: 'none',
    },
}));

export const FillinsChart = () => {
    const dataProvider = useDataProvider();
    const { permissions } = usePermissions();
    const { from, to, chain, origin, stationIds } = useDashboardContext();

    const initialChart = getInitialChart(from, to, initialData);

    const [chartColor, setChartColor] = useState(colors[0]);
    const [chartData, setChartData] = useState(initialChart);
    const [chartType, setChartType] = useState('totalValue');

    const chainId = permissions && permissions.includes(PERMISSIONS.ADMIN) ? chain : getChainId();

    useEffect(() => {
        const filter = { from, to, chainId, origin };
        if (stationIds && stationIds.length > 0) {
            filter.stationIds = stationIds;
        }
        if (getEmployeeType() === EmployeeType.attendant) {
            filter.attendantId = getAttendantId();
        }

        dataProvider
            .getList('dashboard/fillins-summary', { filter })
            .then(({ data }) => setChartData(data));
    }, [from, to, chain, origin, stationIds]);

    const formatter = useCallback((value) => {
        switch (chartType) {
            case 'totalAmount':
                return formatNumber(value, { style: 'decimal', unit: 'liter' });
            case 'totalFillins':
                return `${formatNumber(value)} Abastecimentos`;
            default:
                return formatNumber(value, { style: 'currency', currency: 'BRL' });
        }
    }, [chartType]);

    const tooltipContent = useCallback(({ payload, label }) => {
        if (!payload || payload.length === 0) return null;
        return (
            <div style={tooltipStyle}>
                <p style={tooltipLabelStyle}>
                    {moment(label, 'DD/MM/YYYY').format('dddd, DD MMM YYYY')}
                </p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ margin: '5px 0', color: entry.color }}>
                        {entry.name}: <strong>{formatter(entry.value)}</strong>
                    </p>
                ))}
            </div>
        );
    }, [formatter]);

    const activeDot = useMemo(() => ({ r: 6, stroke: chartColor, strokeWidth: 2 }), [chartColor]);

    const chartContent = useMemo(() => (
        <AreaChart data={chartData} margin={chartMargin}>
            <YAxis
                domain={yAxisDomain}
                type="number"
                tick={axisTick}
                axisLine={axisLine}
                tickLine={tickLine}
                tickFormatter={formatNumber}
            />
            <XAxis
                dataKey="date"
                tick={<RotatedAxisTick fontSize={12} />}
                axisLine={axisLine}
                tickLine={tickLine}
                height={60}
            />
            <Tooltip content={tooltipContent} />
            <defs>
                {colors.map((color, i) => (
                    <linearGradient key={i} id={color} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.75} />
                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                ))}
            </defs>
            <Area
                stroke={chartColor}
                fillOpacity={0.2}
                fill={`url(#${chartColor})`}
                dataKey={chartType}
                name="Total"
                type="monotone"
                isAnimationActive={true}
                strokeWidth={3}
                activeDot={activeDot}
            />
        </AreaChart>
    ), [chartData, chartType, chartColor, tooltipContent, activeDot]);

    const handleRadioChange = useCallback(({ target: { value } }) => {
        setChartType(value);
        setChartColor(colors[chartTypes.indexOf(value)]);
    }, []);

    const filtersContent = useMemo(() => (
        <FormControl style={filterStyle}>
            <StyledRadioGroup
                row
                defaultValue="totalValue"
                onChange={handleRadioChange}
            >
                {chartLabels.map((label, index) => (
                    <FormControlLabel
                        key={index}
                        value={chartTypes[index]}
                        label={label}
                        control={<Radio />}
                        className={chartType === chartTypes[index] ? 'Mui-checked' : ''}
                    />
                ))}
            </StyledRadioGroup>
        </FormControl>
    ), [handleRadioChange, chartType]);

    return (
        <DashboardWrapper
            permission={PERMISSIONS.FILLINS_CHART}
            filters={filtersContent}
            aspectRatio={6.0 / 2.5}
            content={chartContent}
        />
    );
};
